import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Sun, Moon } from 'lucide-react';
import { companyInfo } from '../mock';
import { useTheme } from '../context/ThemeContext';

const navItems = [
  { label: 'Work', target: 'featured-work' },
  { label: 'Before & After', target: 'before-after' },
  { label: 'Videos', target: 'video-showcase' },
  { label: 'Services', target: 'services' },
  { label: 'About', target: 'about' },
  { label: 'FAQ', target: 'process-faq' },
];

const Header = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const { theme, toggleTheme } = useTheme();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const scrollToSection = useCallback((id) => {
    setMobileOpen(false);
    if (location.pathname !== '/') {
      window.location.href = `/#${id}`;
      return;
    }
    const el = document.getElementById(id);
    if (el) {
      const offset = 72;
      const y = el.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  }, [location.pathname]);

  const handleLogoClick = (e) => {
    e.preventDefault();
    setMobileOpen(false);
    if (location.pathname !== '/') {
      window.location.href = '/';
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? 'glass-nav shadow-lg' : 'glass-nav'
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div className="flex items-center justify-between h-[68px]">
          {/* Logo - scrolls to hero / navigates home */}
          <a href="/" onClick={handleLogoClick} className="flex flex-col group cursor-pointer" data-testid="nav-logo">
            <span className="font-serif text-[#D4AF37] font-semibold text-xl tracking-wide transition-colors duration-300 group-hover:text-[#E5C158]">
              {companyInfo.name}
            </span>
            <span className="text-[10px] tracking-[0.2em] uppercase text-[#64748B] font-sans font-medium">
              {companyInfo.tagline}
            </span>
          </a>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-1">
            {navItems.map((item) => (
              <button
                key={item.target}
                onClick={() => scrollToSection(item.target)}
                data-testid={`nav-link-${item.target}`}
                className="px-4 py-2 text-[13px] font-sans font-medium tracking-wide text-[#94A3B8] hover:text-[#D4AF37] transition-colors duration-300 cursor-pointer"
              >
                {item.label}
              </button>
            ))}
          </nav>

          {/* Right side: Theme Toggle + CTA */}
          <div className="flex items-center gap-3">
            {/* Theme Toggle */}
            <button
              onClick={toggleTheme}
              data-testid="theme-toggle"
              className="w-9 h-9 rounded-full flex items-center justify-center text-[#94A3B8] hover:text-[#D4AF37] transition-all duration-200 hover:bg-[#D4AF37]/10"
              aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
            >
              {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
            </button>

            {/* CTA */}
            <button
              onClick={() => scrollToSection('contact')}
              data-testid="cta-inquiry-button"
              className="hidden lg:inline-flex items-center px-6 py-2.5 bg-[#D4AF37] text-[#0A0B0E] rounded-full text-[13px] font-sans font-semibold tracking-wide btn-premium"
            >
              Get In Touch
            </button>

            {/* Mobile Toggle */}
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="lg:hidden text-[#D4AF37] p-2"
              aria-label="Toggle menu"
              data-testid="mobile-menu-toggle"
            >
              {mobileOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>

        {/* Mobile Nav */}
        <div className={`lg:hidden overflow-hidden transition-all duration-300 ease-out ${
          mobileOpen ? 'max-h-[500px] opacity-100 pb-6' : 'max-h-0 opacity-0'
        }`}>
          <nav className="flex flex-col gap-1 pt-2 border-t border-[rgba(212,175,55,0.1)]">
            {navItems.map((item) => (
              <button
                key={item.target}
                onClick={() => scrollToSection(item.target)}
                data-testid={`nav-link-${item.target}-mobile`}
                className="px-4 py-3 text-left text-sm font-sans font-medium text-[#94A3B8] hover:text-[#D4AF37] hover:bg-[#12141A] rounded-lg transition-all duration-200"
              >
                {item.label}
              </button>
            ))}
            <button
              onClick={() => scrollToSection('contact')}
              data-testid="cta-inquiry-button-mobile"
              className="mt-2 px-4 py-3 bg-[#D4AF37] text-[#0A0B0E] rounded-full text-sm font-sans font-semibold text-center"
            >
              Get In Touch
            </button>
          </nav>
        </div>
      </div>
    </header>
  );
};

export default Header;
